import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { sgwtConnect } from '@/sgwt/sgwtConnect.ts';
import type { SGWTConnectCore, SGWTConnectError } from '@sgwt/connect-core';
import { type SgwtConnectHTMLElement, SgwtWidgetContextProvider } from '@sgwt/sgwt-widgets-react';
import { RouterProvider, createRouter } from '@tanstack/react-router';

import { IntlProvider } from '@/context/IntlContext.tsx';
import 'ag-grid-community';
import 'ag-grid-enterprise';
import 'ag-grid-react';
// Import the generated route tree
import { Provider } from 'react-redux';
import { setupAgGridLicence } from './licenceAgGrid.ts';
import { routeTree } from './routeTree.gen';
import type { SgWidgetsBus } from './sgwt/widgets.ts';
import { type AppStore, store } from './store/store.ts';
import { type UserAuthorization, hasSgpbCockpitIAMPermissions } from './utils/iamPermissions.ts';
export const BUS_TOPIC_SGCONNECT_USERINFO = 'sg-connect.user-info';

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

setupSgwtConnectWidget(sgwtConnect);
setupAgGridLicence();

async function enableMocking() {
  if (import.meta.env.VITE_USE_MOCK === 'true') {
    const { worker } = await import('./mocks/browser');

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return worker.start();
  }
  return;
}

export function getWidgetBus(): SgWidgetsBus | null {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const widgetConfiguration = (window as any).SGWTWidgetConfiguration;
  return widgetConfiguration?.bus ? widgetConfiguration.bus : null;
}

function setupAppAndRender(store: AppStore) {
  if (sgwtConnect.isAuthorized()) {
    const bus = getWidgetBus();
    if (bus) {
      bus.subscribe<UserAuthorization>(BUS_TOPIC_SGCONNECT_USERINFO, (userInfo) =>
        hasSgpbCockpitIAMPermissions(store.dispatch, userInfo),
      );
    }
    renderApp(store);
  } else {
    const authorizationError = sgwtConnect.getAuthorizationError();
    if (authorizationError !== null) {
      renderError(authorizationError);
    } else {
      sgwtConnect.requestAuthorization();
    }
  }
}

function renderApp(store: AppStore) {
  const rootElement = document.getElementById('root')!;
  const root = createRoot(rootElement);

  enableMocking().then(() => {
    root.render(
      <StrictMode>
        <Provider store={store}>
          <SgwtWidgetContextProvider infrastructure="azure">
            <IntlProvider>
              <RouterProvider router={router} />
            </IntlProvider>
          </SgwtWidgetContextProvider>
        </Provider>
      </StrictMode>,
    );
  });
}

function renderError(authorizationError: SGWTConnectError) {
  document.body.innerHTML = `
      <div class='alert alert-danger' role='alert'>
        Authorization error: ${authorizationError}.
      </div>`;
}

function setupSgwtConnectWidget(sgwtConnect: SGWTConnectCore) {
  const widget = document.querySelector<SgwtConnectHTMLElement>('sgwt-connect');

  if (widget) {
    // When the code is executed, the swgtConnectWidget may not have been initialized. So, we need to check that, otherwise calling
    // `swgtConnectWidget.setSgwtConnectInstance()` will throw an error.
    if (typeof widget.setSgwtConnectInstance === 'undefined') {
      // Widget is not initialized yet, so we will wait the event that indicates the swgtConnectWidget is ready...
      const handleSgwtConnectReady = () => {
        widget.removeEventListener('sgwt-connect--ready', handleSgwtConnectReady);
        widget.setSgwtConnectInstance(sgwtConnect);
      };

      widget.addEventListener('sgwt-connect--ready', handleSgwtConnectReady);
    } else {
      // Widget is initialized...
      widget.setSgwtConnectInstance(sgwtConnect);
    }
  }
}

/**
 * Start the APP
 */
setupAppAndRender(store);
